// eslint-disable-next-line import/no-extraneous-dependencies
import React, {
    HTMLAttributes,
    ReactElement, ReactNode, useMemo,
    JSX,
} from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { CSS } from "@dnd-kit/utilities";
// eslint-disable-next-line import/no-extraneous-dependencies
import { useSortable } from "@dnd-kit/sortable";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { DraggableAttributes } from "@dnd-kit/core";
import { useCombinedRef } from "../../useOrCreateRef";

type AsProp = keyof JSX.IntrinsicElements;

export interface SortableItemProps extends HTMLAttributes<HTMLElement> {
    item: {id: string},
    nodeType?: AsProp;
    children?: ReactNode,
    shouldDragWholeElement?: boolean;
    isEnabled?: boolean;
}

interface SortableItemState {
    listeners?: SyntheticListenerMap;
    attributes?: DraggableAttributes;
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
}
export const SortableItemStateContext = React.createContext<SortableItemState>({
    listeners: undefined,
    attributes: undefined,
    setActivatorNodeRef: undefined,
});

const SortableItem = React.forwardRef((
    args: SortableItemProps,
    ref: React.ForwardedRef<HTMLElement>,
): ReactElement => {
    const {
        children,
        item,
        nodeType,
        shouldDragWholeElement,
        isEnabled,
        ...rest
    } = args;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        setActivatorNodeRef,
    } = useSortable({ id: item.id, data: item, disabled: !isEnabled });

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const listenerState = { listeners, attributes, setActivatorNodeRef };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const mergedRef = useCombinedRef<HTMLElement | null>([ref, setNodeRef]);

    const Element = (nodeType ?? "div") as React.ElementType;

    const listenerExpand = shouldDragWholeElement ? { ...listeners, ...attributes } : {};
    const element = (
        <Element
            key={item.id}
            ref={mergedRef}
            style={style}
            {...rest}
            {...listenerExpand}
        >
            {children}
        </Element>
    );

    return shouldDragWholeElement ? element : (
        <SortableItemStateContext.Provider value={listenerState}>
            {element}
        </SortableItemStateContext.Provider>
    );
});

SortableItem.defaultProps = {
    nodeType: "div",
    shouldDragWholeElement: true,
    isEnabled: true,
};

export default SortableItem;
